import { NumberDecimalFormat } from '@components/shared/Format'
import { Constants } from '@models/Constants'
import { Country, CountryData2024 } from '@models/Country'
import { Store } from '@stores/Store'
import { FqGrowthValue, LevelAchievedValue } from '@utils/sections/FrontlineGrowthIncentive.util'

export class FrontlineGrowthIncentiveCalculation {
  store!: Store

  constructor(store: Store) {
    this.store = store
  }

  // Frontline Growth Incentive

  get frontlineGrowthIncentiveAnnualMultipier(): number {
    const { store } = this
    const { countryData, frontlineGrowthIncentiveStore } = store
    const { frontlineGrowthIncentiveFqGrowth } = frontlineGrowthIncentiveStore

    if (!this.frontlineGrowthIncentiveAllCriteriaMet) { return 0 }

    const data = countryData as CountryData2024

    /* eslint-disable indent */
    switch (frontlineGrowthIncentiveFqGrowth) {
      case FqGrowthValue.MAINTAIN: { return data.frontlineGrowthIncentive.multiplierMaintain }
      case FqGrowthValue.FQS_1_TO_2: { return data.frontlineGrowthIncentive.multiplier1to2! }
      case FqGrowthValue.FQS_3_TO_5: { return data.frontlineGrowthIncentive.multiplier3to5! }
      case FqGrowthValue.FQS_6_PLUS: { return data.frontlineGrowthIncentive.multiplier6plus! }
      default: { return 0 }
    }
  }

  get frontlineGrowthIncentive(): number {
    const { store } = this
    const { frontlineGrowthIncentiveStore } = store
    const { frontlineGrowthIncentiveActive } = frontlineGrowthIncentiveStore

    if (!frontlineGrowthIncentiveActive) { return 0 }

    return this.frontlineGrowthIncentiveAnnualIncentiveAmount
  }

  get frontlineGrowthIncentiveAnnualIncentiveAmount(): number {
    const annualSumAmountWithMultiplier = this.frontlineGrowthIncentiveAnnualSumOfLbFlbDepth * this.frontlineGrowthIncentiveAnnualMultipier
    if (this.frontlineGrowthIncentiveAllCriteriaMet && annualSumAmountWithMultiplier > 0) {
      return Math.min(NumberDecimalFormat(annualSumAmountWithMultiplier), NumberDecimalFormat(this.frontlineGrowthIncentivePaymentCap))
    }

    return 0
  }

  get frontlineGrowthIncentiveAnnualPPVCriteria(): boolean {
    const { store } = this
    const { personalAnnualPV, globalStore, frontlineGrowthIncentiveStore } = store
    const { country } = globalStore
    const { frontlineGrowthIncentiveActive } = frontlineGrowthIncentiveStore

    if (!frontlineGrowthIncentiveActive) { return false }
    if (
      (personalAnnualPV >= Constants.ANNUAL_PPV_EUROPE && (country !== Country.AU && country !== Country.NZ)) ||
      (personalAnnualPV >= Constants.ANNUAL_PPV_ANZ && (country === Country.AU || country === Country.NZ))
    ) {
      return true
    }

    return false
  }

  get frontlineGrowthIncentiveAnnualSumOfLbFlbDepth(): number {
    return this.frontlineGrowthIncentiveMonthlySumOfLbFlbDepth * 12
  }

  get frontlineGrowthIncentiveMonthlySumOfLbFlbDepth(): number {
    const { store } = this
    const { leadershipBonus, fosterBonus, depthCalculation } = store
    const { depthBonus } = depthCalculation

    return NumberDecimalFormat(leadershipBonus + fosterBonus + depthBonus)
  }

  get frontlineGrowthIncentiveAllCriteriaMet(): boolean {
    return this.frontlineGrowthIncentiveLevelEligibility && this.frontlineGrowthIncentiveAnnualPPVCriteria && this.frontlineGrowthIncentiveRubyPvCriteria
  }

  get frontlineGrowthIncentiveRubyPvCriteria(): boolean {
    const { store } = this
    const { rubyPV } = store

    return (rubyPV * 12) >= this.frontlineGrowthIncentiveAnnualRubyPVRequirement
  }

  get frontlineGrowthIncentiveLevelEligibility(): boolean {
    const { store } = this
    const { frontlineGrowthIncentiveStore } = store
    const { frontlineGrowthIncentiveLevelAchieved } = frontlineGrowthIncentiveStore

    const frontlineGrowthIncentiveLevel = frontlineGrowthIncentiveLevelAchieved
    return frontlineGrowthIncentiveLevel !== LevelAchievedValue.BELOW_F_PLATINUM
  }

  get frontlineGrowthIncentiveAnnualPPVRequirement(): number {
    const { store } = this
    const { countryData } = store

    return countryData.frontlineGrowthIncentiveAnnualVolume!
  }

  get frontlineGrowthIncentiveAnnualRubyPVRequirement(): number {
    const { store } = this
    const { countryData, frontlineGrowthIncentiveStore } = store
    const { frontlineGrowthIncentiveLevelAchieved } = frontlineGrowthIncentiveStore

    if (frontlineGrowthIncentiveLevelAchieved !== LevelAchievedValue.DIAMOND_AND_ABOVE) {
      return countryData.frontlineGrowthIncentiveRubyPVRequirement!
    }

    return 0
  }

  get frontlineGrowthIncentivePaymentCap(): number {
    const { store } = this
    const { countryData } = store

    return countryData.frontlineGrowthIncentiveAmountCap!
  }

}
